import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { StatCard } from '../components/dashboard/StatCard';
import { RecentTransactions } from '../components/dashboard/RecentTransactions';
import { QuickActions } from '../components/dashboard/QuickActions';
import { CashFlowChart } from '../components/dashboard/CashFlowChart';
import { api } from '../services/api';

interface DashboardStats {
  totalRevenue: number;
  totalExpenses: number;
  netProfit: number;
  pendingInvoices: number;
  overdueInvoices: number;
  totalCustomers: number;
  totalVendors: number;
  recentTransactions: Transaction[];
}

interface Transaction {
  id: string;
  description: string;
  amount: number;
  type: 'INCOME' | 'EXPENSE';
  date: string;
  category: string;
}

interface CashFlowData {
  month: string;
  income: number;
  expenses: number;
  netCashFlow: number;
}

const GoldSkeleton: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <div
    style={{
      background: 'linear-gradient(90deg, #f4efe2 25%, #e3dcc4 50%, #f4efe2 75%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.8s infinite',
      borderRadius: 6,
      ...style,
    }}
  />
);

export const Dashboard: React.FC = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async (): Promise<DashboardStats> => {
      const response = await api.get('/dashboard/stats');
      return response.data.data;
    }
  });

  const { data: cashFlowData, isLoading: cashFlowLoading } = useQuery({
    queryKey: ['dashboard-cashflow'],
    queryFn: async (): Promise<CashFlowData[]> => {
      const response = await api.get('/dashboard/cashflow?period=6months');
      const data = response.data.data;
      const months = data.income.map((income: any) => income.label);
      return months.map((month: string) => {
        const incomeItem = data.income.find((item: any) => item.label === month);
        const expenseItem = data.expenses.find((item: any) => item.label === month);
        const netItem = data.netCashFlow.find((item: any) => item.label === month);
        return {
          month,
          income: incomeItem?.value || 0,
          expenses: expenseItem?.value || 0,
          netCashFlow: netItem?.value || 0,
        };
      });
    }
  });

  if (isLoading || !stats) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* Header skeleton */}
        <div>
          <GoldSkeleton style={{ height: 14, width: 200, marginBottom: 8 }} />
          <GoldSkeleton style={{ height: 10, width: 300 }} />
        </div>

        {/* Stat cards skeleton */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <GoldSkeleton style={{ height: 10, width: '60%', marginBottom: 16 }} />
              <GoldSkeleton style={{ height: 32, width: '80%', marginBottom: 8 }} />
              <GoldSkeleton style={{ height: 8, width: '40%' }} />
            </div>
          ))}
        </div>

        {/* Chart skeleton */}
        <div style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <GoldSkeleton style={{ height: 12, width: 160, marginBottom: 20 }} />
          <GoldSkeleton style={{ height: 240 }} />
        </div>

        {/* Bottom grid skeleton */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
          <div style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <GoldSkeleton style={{ height: 12, width: 160, marginBottom: 20 }} />
            {[1, 2, 3, 4].map(i => <GoldSkeleton key={i} style={{ height: 44, marginBottom: 10 }} />)}
          </div>
          <div style={{ background: '#ffffff', border: '1px solid #e3dcc4', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <GoldSkeleton style={{ height: 12, width: 120, marginBottom: 20 }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {[1, 2, 3, 4].map(i => <GoldSkeleton key={i} style={{ height: 72 }} />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const mainStats = [
    {
      title: 'Total Revenue', value: stats.totalRevenue ?? 0, format: 'currency' as const, color: 'blue' as const,
      icon: <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" /></svg>,
    },
    {
      title: 'Total Expenses', value: stats.totalExpenses ?? 0, format: 'currency' as const, color: 'red' as const,
      icon: <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v2a2 2 0 002 2z" /></svg>,
    },
    {
      title: 'Net Profit', value: stats.netProfit ?? 0, format: 'currency' as const,
      color: ((stats.netProfit ?? 0) >= 0 ? 'green' : 'red') as 'green' | 'red',
      icon: <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
    },
    {
      title: 'Pending Invoices', value: stats.pendingInvoices ?? 0, format: 'number' as const, color: 'yellow' as const,
      icon: <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,
    },
  ];

  const secondaryStats = [
    {
      title: 'Overdue Invoices', value: stats.overdueInvoices ?? 0, format: 'number' as const, color: 'red' as const,
      icon: <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    },
    {
      title: 'Total Customers', value: stats.totalCustomers ?? 0, format: 'number' as const, color: 'green' as const,
      icon: <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    },
    {
      title: 'Total Vendors', value: stats.totalVendors ?? 0, format: 'number' as const, color: 'purple' as const,
      icon: <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      {/* Page header */}
      <div style={{ paddingBottom: 20, borderBottom: '1px solid #e3dcc4' }}>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a8778', marginBottom: 6 }}>
          Overview
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 32, fontWeight: 800, color: '#1c2033', lineHeight: 1, letterSpacing: '-0.02em' }}>
          Financial Summary
        </div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 14, color: '#6b6a5c', marginTop: 6 }}>
          Here's what's happening with your business today.
        </div>
      </div>

      {/* Primary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
        {mainStats.map((s, i) => (
          <StatCard key={i} {...s} />
        ))}
      </div>

      {/* Secondary stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
        {secondaryStats.map((s, i) => (
          <StatCard key={i} {...s} />
        ))}
      </div>

      {/* Cash flow chart */}
      <CashFlowChart data={cashFlowData ?? []} isLoading={cashFlowLoading} />

      {/* Transactions + Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        <RecentTransactions transactions={stats.recentTransactions ?? []} />
        <QuickActions />
      </div>
    </div>
  );
};
